import { IItem } from '@/interfaces/IItem';
import { IOption } from '@/interfaces/IOption';
import { IOptionItem } from '@/interfaces/IOptionItem';
import { itemToItemOptions } from '@/utils/orders.utils';
import type { IMenuOption, IMenuOptionItem } from './pos-types';

/**
 * Adapts admin catalog entities (IOption/IOptionItem, with item-option
 * overrides already merged by `itemToItemOptions`) onto the -pos IMenuOption
 * shape consumed by the ported pricing/validation/formatting domain.
 *
 * Coercions:
 * - IOptionItem.default is a number (0/1) → boolean
 * - IOptionItem.min/max are nullable → 0 (meaning "no limit", same as -pos)
 * - discountedCost undefined → null
 *
 * Filtering: inactive option groups and inactive option items are excluded,
 * matching -pos `useMenuService.getItemOptions` (the domain layer is designed
 * to only ever receive active entries).
 */
export function adaptOptionItem(optionItem: IOptionItem): IMenuOptionItem {
  return {
    id: optionItem.id ?? 0,
    title: optionItem.title,
    cost: optionItem.cost,
    discountedCost: optionItem.discountedCost ?? null,
    min: optionItem.min ?? 0,
    max: optionItem.max ?? 0,
    default: !!optionItem.default,
    active: optionItem.active,
    index: optionItem.index ?? 0,
  };
}

export function adaptOption(option: IOption): IMenuOption | null {
  if (typeof option.id !== 'number') return null;
  if (!option.active) return null;

  return {
    id: option.id,
    type: String(option.type ?? ''),
    title: option.title,
    mandatory: !!option.mandatory,
    multiple: !!option.multiple,
    min: option.min ?? 0,
    max: option.max ?? 0,
    index: option.index ?? 0,
    active: option.active,
    main: !!option.main,
    items: (option.optionItems ?? [])
      .filter((oi) => typeof oi.id === 'number' && oi.active)
      .map(adaptOptionItem),
  };
}

/**
 * Full item → domain option groups (merging item-option overrides), ready for
 * `computeItemUnitPrice` / `validateSelectedOptions` / `formatCartItemOptions`.
 */
export function adaptItemOptions(item: IItem): IMenuOption[] {
  const merged = itemToItemOptions(item);

  const out: IMenuOption[] = [];
  for (const option of merged) {
    const adapted = adaptOption(option);
    if (adapted) out.push(adapted);
  }
  return out;
}
